import { useNavigate } from 'react-router-dom';
import { scrollToSection } from '@/utils/scrollToSection';
import { BLOG_URL, CAREERS_URL } from '@/constants/links';
import FooterNewsletter from './FooterNewsletter';

const serviceLinks = [
  { label: 'Smart Fulfillment', anchor: 'smart-fulfillment' },
  { label: 'Flexible Storage', anchor: 'flexible-storage' },
  { label: 'Inventory Management', anchor: 'inventory-management' },
  { label: 'Last-Mile Delivery Coordination', anchor: 'last-mile-delivery' },
  { label: 'Ecommerce Fulfillment', anchor: 'ecommerce-fulfillment' },
  { label: 'Supply Chain Consulting', anchor: 'consulting' },
];

export default function Footer() {
  const navigate = useNavigate();

  const handleServiceClick = (anchor: string) => {
    navigate('/services');
    scrollToSection(anchor);
  };

  return (
    <footer style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
      <FooterNewsletter />
      <div className="max-w-7xl mx-auto px-6 pt-16 pb-8" style={{ background: '#0d0d14' }}>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 mb-12">

          {/* Brand */}
          <div>
            <div className="flex items-center mb-4">
              <img
                src="https://public.readdy.ai/ai/img_res/edited_2327a554216bf9e95ffa821d05c0e0af_e62d8e34.jpg"
                alt="The Hub Terminus Limited"
                className="h-10 w-auto object-contain"
                style={{
                  mixBlendMode: 'screen',
                  filter: 'brightness(1.15)',
                }}
              />
            </div>
            <p className="text-white/50 text-sm leading-relaxed mb-5">
              Nigeria&apos;s technology-driven fulfillment platform. Smart storage, intelligent logistics, real-time visibility.
            </p>
            <div className="flex items-center gap-3">
              {[
                { icon: 'ri-linkedin-fill', label: 'LinkedIn', href: 'https://www.linkedin.com/company/thl-the-hub-terminus-limited/' },
                { icon: 'ri-instagram-line', label: 'Instagram', href: 'https://www.instagram.com/thlglobal_' },
                { icon: 'ri-facebook-fill', label: 'Facebook', href: 'https://www.facebook.com/thehubterminuslimited' },
                { icon: 'ri-twitter-x-line', label: 'X', href: 'https://x.com/thlglobal_' },
              ].map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="nofollow noreferrer"
                  aria-label={s.label}
                  className="w-9 h-9 rounded-lg bg-white/5 hover:bg-thl-red/20 hover:text-thl-red text-white/60 transition-all cursor-pointer flex items-center justify-center"
                >
                  <i className={`${s.icon} text-base`} />
                </a>
              ))}
            </div>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-white text-sm font-bold mb-4 uppercase tracking-wide">Services</h4>
            <div className="flex flex-col gap-2">
              {serviceLinks.map(s => (
                <button
                  key={s.anchor}
                  onClick={() => handleServiceClick(s.anchor)}
                  className="text-left text-white/50 hover:text-thl-red text-sm transition-colors cursor-pointer"
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          {/* Company */}
          <div>
            <h4 className="text-white text-sm font-bold mb-4 uppercase tracking-wide">Company</h4>
            <div className="flex flex-col gap-2">
              {[
                { label: 'About THL', path: '/about' },
                { label: 'Technology', path: '/technology' },
                { label: 'Facilities', path: '/facilities' },
                { label: 'Industries', path: '/industries' },
                { label: 'Careers', href: CAREERS_URL, external: true },
                { label: 'Fulfilment intelligence', href: BLOG_URL, external: true },
              ].map(item => (
                <button
                  key={'external' in item ? item.href : item.path}
                  onClick={() => {
                    if ('external' in item) {
                      window.open(item.href, '_blank', 'noopener,noreferrer');
                      return;
                    }
                    navigate(item.path);
                  }}
                  className="text-left text-white/50 hover:text-thl-red text-sm transition-colors cursor-pointer"
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-white text-sm font-bold mb-4 uppercase tracking-wide">Contact</h4>
            <div className="flex flex-col gap-3 text-sm text-white/50">
              <p>📍 15 Kudirat Abiola Way Oregun, Lagos</p>
              <p>📞 07003000009</p>
              <p>✉ info@thehubng.com</p>
              <a href="https://www.thehubng.com" target="_blank" rel="noopener noreferrer" className="hover:text-thl-red transition-colors">🌐 www.thehubng.com</a>
              <p>🕐 Mon–Fri: 8am–5pm · Sat: 9am–3pm</p>
            </div>
            <button
              onClick={() => navigate('/contact')}
              className="mt-4 text-thl-red text-sm font-medium hover:underline cursor-pointer"
            >
              → Book a Tour
            </button>
          </div>
        </div>

        <div className="border-t border-white/5 pt-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <span className="text-white/30 text-xs">
            © 2026 The Hub Terminus Limited. All rights reserved.
          </span>
          <span className="text-white/30 text-xs">People · Service · Technology</span>
        </div>
      </div>
    </footer>
  );
}
